import { useEffect, useState } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import { apiFetch } from '../../api/client';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  createdAt: string;
}

interface UserListResponse {
  users: User[];
  total: number;
  page: number;
  limit: number;
}

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-amber-100 text-amber-700',
  suspended: 'bg-red-100 text-red-700',
};

const ROLE_META: Record<string, { label: string; icon: string; color: string }> = {
  issuer:   { label: 'Issuers',   icon: 'verified',    color: 'text-blue-600' },
  verifier: { label: 'Verifiers', icon: 'fact_check',  color: 'text-green-600' },
  holder:   { label: 'Holders',   icon: 'badge',       color: 'text-slate-600' },
  admin:    { label: 'Admins',    icon: 'admin_panel_settings', color: 'text-purple-600' },
};

const PAGE_SIZE = 50;

function formatDate(iso: string) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return iso; }
}

export default function AdminUserDirectory() {
  const { role } = useParams<{ role: string }>();
  const validRole = role && ROLE_META[role] ? role : 'issuer';
  const meta = ROLE_META[validRole];

  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setPage(1);
    setSearch('');
  }, [validRole]);

  useEffect(() => {
    setLoading(true);
    setError(false);
    apiFetch<UserListResponse>(`/admin/users?role=${validRole}&limit=${PAGE_SIZE}&page=${page}`)
      .then((data) => {
        setUsers(data.users ?? []);
        setTotal(data.total ?? 0);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [validRole, page]);

  const filtered = search.trim()
    ? users.filter((u) =>
        u.name.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase())
      )
    : users;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-main flex items-center gap-2">
            <span className={`material-symbols-outlined ${meta.color}`}>{meta.icon}</span>
            {meta.label}
          </h1>
          <p className="mt-1 text-sm text-text-muted">
            {total} registered {meta.label.toLowerCase()}
          </p>
        </div>

        {/* Role tabs */}
        <div className="flex gap-2">
          {(['issuer', 'verifier', 'holder'] as const).map((r) => (
            <NavLink
              key={r}
              to={`/admin/users/${r}`}
              className={({ isActive }) =>
                `rounded-full px-4 py-1.5 text-xs font-bold capitalize transition-colors ${
                  isActive
                    ? 'bg-primary text-white'
                    : 'bg-slate-100 text-text-faint hover:bg-slate-200'
                }`
              }
            >
              {ROLE_META[r].label}
            </NavLink>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <div className="stat-card">
          <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted">Total</span>
          <div className="mt-2"><span className="text-3xl font-bold text-text-main">{total}</span></div>
        </div>
        <div className="stat-card">
          <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted">Active</span>
          <div className="mt-2">
            <span className="text-3xl font-bold text-text-main">
              {users.filter((u) => u.status === 'active').length}
            </span>
          </div>
        </div>
        <div className="stat-card">
          <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted">Pending</span>
          <div className="mt-2">
            <span className="text-3xl font-bold text-text-main">
              {users.filter((u) => u.status === 'pending').length}
            </span>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-text-faint" style={{ fontSize: 18 }}>search</span>
        <input
          type="text"
          placeholder={`Search ${meta.label.toLowerCase()} by name or email…`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-lg border border-border-subtle bg-white pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-border-subtle bg-white shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : error ? (
          <div className="py-20 text-center text-sm text-red-500">Failed to load users.</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-20 text-text-faint">
            <span className="material-symbols-outlined text-4xl">{meta.icon}</span>
            <p className="text-sm font-medium">No {meta.label.toLowerCase()} found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-subtle bg-slate-50/50">
                <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">Name</th>
                <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">Email</th>
                <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">Status</th>
                <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {filtered.map((user) => (
                <tr key={user.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex size-8 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700 shrink-0">
                        {user.name ? user.name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) : '??'}
                      </div>
                      <span className="min-w-0 truncate text-sm font-medium text-text-main" title={user.name || undefined}>
                        {user.name || '—'}
                      </span>
                    </div>
                  </td>
                  {/* Long addresses used to stretch the row past the viewport.
                      break-all wraps them; the full value stays in the title. */}
                  <td className="max-w-[280px] px-6 py-4 text-sm text-text-muted">
                    <span className="block break-all" title={user.email}>{user.email}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-bold capitalize ${STATUS_COLORS[user.status] ?? 'bg-slate-100 text-slate-600'}`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-text-muted">{formatDate(user.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-text-muted">
          <span>Page {page} of {totalPages} ({total} total)</span>
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="rounded-lg border border-border-subtle px-3 py-1.5 text-xs font-bold disabled:opacity-40 hover:bg-slate-50"
            >
              Previous
            </button>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-lg border border-border-subtle px-3 py-1.5 text-xs font-bold disabled:opacity-40 hover:bg-slate-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
